import { useEffect, useState } from 'react'
import { Link as RouterLink, NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import MenuIcon from '@mui/icons-material/Menu'
import DashboardIcon from '@mui/icons-material/Dashboard'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import EventNoteIcon from '@mui/icons-material/EventNote'
import HikingIcon from '@mui/icons-material/Hiking'
import SyncAltIcon from '@mui/icons-material/SyncAlt'
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import LocalCafeIcon from '@mui/icons-material/LocalCafe'
import RateReviewIcon from '@mui/icons-material/RateReview'
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary'
import GroupIcon from '@mui/icons-material/Group'
import LogoutIcon from '@mui/icons-material/Logout'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'

import { AdminLoginPage } from './AdminLoginPage'
import { AdminDashboard } from './AdminDashboard'
import { AdminCalendarPage } from './AdminCalendarPage'
import { AdminBookingsPage } from './AdminBookingsPage'
import { AdminActivitiesPage } from './AdminActivitiesPage'
import { AdminFeedsPage } from './AdminFeedsPage'
import { AdminOrdersPage } from './AdminOrdersPage'
import { AdminBillingPage } from './AdminBillingPage'
import { AdminServicesPage } from './AdminServicesPage'
import { AdminFeedbackPage } from './AdminFeedbackPage'
import { AdminPicturesPage } from './AdminPicturesPage'
import { AdminUsersPage } from './AdminUsersPage'
import { authApi } from '../../api/endpoints'
import { isAuthenticated } from '../../api/client'
import { PageMeta } from '../../components/common/PageMeta'
import { LanguageSwitcher } from '../../components/layout/LanguageSwitcher'
import type { CurrentUser } from '../../types/api'

const DRAWER_WIDTH = 248

/**
 * The owner console.
 *
 * Lazily loaded from the public router, so none of this reaches a guest's phone. It also
 * keeps its own layout rather than reusing the marketing shell: the two have opposite goals,
 * one selling and one operating, and sharing a header would compromise both.
 */
export function AdminApp() {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [user, setUser] = useState<CurrentUser | null>(null)
  const [checking, setChecking] = useState(true)
  const location = useLocation()

  // Verify the stored token against the API rather than trusting its expiry alone. A token
  // signed by a previous deployment's secret parses locally but is rejected on first use,
  // and finding that out at sign-in is better than mid-edit.
  useEffect(() => {
    if (!isAuthenticated()) {
      setChecking(false)
      return
    }
    authApi
      .me()
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setChecking(false))
  }, [])

  const navItems = [
    { to: '/admin', end: true, icon: <DashboardIcon />, label: t('admin.dashboard') },
    { to: '/admin/calendar', icon: <CalendarMonthIcon />, label: t('admin.calendar') },
    { to: '/admin/bookings', icon: <EventNoteIcon />, label: t('admin.bookings') },
    { to: '/admin/orders', icon: <RestaurantMenuIcon />, label: t('admin.orders') },
    { to: '/admin/billing', icon: <ReceiptLongIcon />, label: t('admin.billing') },
    { to: '/admin/services', icon: <LocalCafeIcon />, label: t('admin.services') },
    { to: '/admin/pictures', icon: <PhotoLibraryIcon />, label: t('admin.pictures') },
    { to: '/admin/feedback', icon: <RateReviewIcon />, label: t('admin.feedback') },
    { to: '/admin/activities', icon: <HikingIcon />, label: t('admin.activities') },
    { to: '/admin/feeds', icon: <SyncAltIcon />, label: t('admin.feeds') },
    ...(user?.role === 'OWNER'
      ? [{ to: '/admin/users', icon: <GroupIcon />, label: t('admin.users') }]
      : []),
  ]

  if (checking) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100dvh' }}>
        <Typography color="text.secondary">{t('common.loading')}…</Typography>
      </Box>
    )
  }

  if (!user) {
    return <AdminLoginPage onSignedIn={setUser} />
  }

  const signOut = () => {
    authApi.logout()
    setUser(null)
  }

  const drawerContent = (
    <Box sx={{ width: DRAWER_WIDTH }}>
      <Toolbar sx={{ px: 2.5 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>🛶 Lomasaaret</Typography>
          <Typography variant="caption" color="text.secondary">
            {user.fullName} · {user.role}
          </Typography>
        </Box>
      </Toolbar>

      <List sx={{ px: 1 }}>
        {navItems.map((item) => {
          const active = item.end
            ? location.pathname === item.to
            : location.pathname.startsWith(item.to)
          return (
            <ListItemButton
              key={item.to}
              component={NavLink}
              to={item.to}
              end={item.end}
              selected={active}
              onClick={() => setDrawerOpen(false)}
              sx={{ borderRadius: 2.5, mb: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          )
        })}
      </List>

      <Box sx={{ px: 2, mt: 2 }}>
        <Button component={RouterLink} to="/" size="small" fullWidth>
          ← {t('nav.home')}
        </Button>
        <Button onClick={signOut} size="small" fullWidth startIcon={<LogoutIcon />} color="inherit">
          {t('admin.signOut')}
        </Button>
      </Box>
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh', bgcolor: 'background.default' }}>
      <PageMeta title={t('admin.dashboard')} path="/admin" noIndex />

      {isMobile ? (
        <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              borderInlineEnd: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {isMobile && (
          <AppBar position="sticky">
            <Toolbar>
              <IconButton edge="start" onClick={() => setDrawerOpen(true)} aria-label={t('nav.menu')}>
                <MenuIcon />
              </IconButton>
              <Typography sx={{ ms: 1, fontWeight: 700, flex: 1 }}>🛶 Lomasaaret</Typography>
              <LanguageSwitcher compact />
            </Toolbar>
          </AppBar>
        )}

        <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
          <Routes>
            <Route index element={<AdminDashboard />} />
            <Route path="calendar" element={<AdminCalendarPage />} />
            <Route path="bookings" element={<AdminBookingsPage />} />
            <Route path="orders" element={<AdminOrdersPage />} />
            <Route path="billing" element={<AdminBillingPage />} />
            <Route path="services" element={<AdminServicesPage />} />
            <Route path="pictures" element={<AdminPicturesPage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="feedback" element={<AdminFeedbackPage />} />
            <Route path="activities" element={<AdminActivitiesPage />} />
            <Route path="feeds" element={<AdminFeedsPage />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
        </Container>
      </Box>
    </Box>
  )
}
